import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { PictureSliderComponent } from '../../../types/builder';

interface PictureSliderRendererProps {
  component: PictureSliderComponent;
}

export const PictureSliderRenderer: React.FC<PictureSliderRendererProps> = ({ component }) => {
  const { settings } = component;
  const [currentIndex, setCurrentIndex] = useState(0);

  const slides = settings.slides || [];
  const totalSlides = slides.length;

  // Auto-play
  useEffect(() => {
    if (!settings.autoPlay || totalSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, settings.autoPlaySpeed || 5000);

    return () => clearInterval(interval);
  }, [settings.autoPlay, settings.autoPlaySpeed, totalSlides]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const shadowClass = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
    xl: 'shadow-xl',
  }[settings.shadow || 'none'];

  if (totalSlides === 0) {
    return (
      <div 
        className="flex justify-center"
        style={{ padding: `${settings.padding || 16}px` }}
      >
        <div 
          className="bg-gray-800 flex items-center justify-center text-gray-400"
          style={{ 
            width: '100%', 
            maxWidth: '1040px',
            aspectRatio: '1/1',
            borderRadius: `${settings.borderRadius || 12}px`,
          }}
        >
          <span>Add slides to Picture Slider</span>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="flex justify-center"
      style={{ padding: `${settings.padding || 16}px` }}
    >
      <div 
        className={`relative overflow-hidden ${shadowClass}`}
        style={{ 
          width: '100%', 
          maxWidth: '1040px',
          aspectRatio: '1/1',
          borderRadius: `${settings.borderRadius || 12}px`,
        }}
      >
        {/* Slides Container */}
        <div 
          className="absolute inset-0 flex transition-transform duration-500 ease-out"
          style={{ transform: `translateX(-${currentIndex * 100}%)` }}
        >
          {slides.map((slide, index) => {
            const slideContent = (
              <img
                src={slide.src || 'https://placehold.co/1040x1040/1f2937/6b7280?text=Slide'}
                alt={slide.alt || `Slide ${index + 1}`}
                className="w-full h-full object-cover flex-shrink-0"
              />
            );

            return slide.link ? (
              <a
                key={slide.id}
                href={slide.link}
                target={settings.openInNewTab ? '_blank' : '_self'}
                rel={settings.openInNewTab ? 'noopener noreferrer' : undefined}
                className="block flex-shrink-0 w-full h-full"
              >
                {slideContent}
              </a>
            ) : (
              <div key={slide.id} className="flex-shrink-0 w-full h-full">
                {slideContent}
              </div>
            );
          })}
        </div>

        {/* Navigation Arrows */}
        {settings.showArrows && totalSlides > 1 && (
          <>
            <button
              onClick={goToPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all"
            >
              <ChevronLeft size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 text-white flex items-center justify-center transition-all"
            >
              <ChevronRight size={24} />
            </button>
          </>
        )}

        {/* Dots Indicator */}
        {settings.showDots && totalSlides > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`h-2 rounded-full transition-all ${
                  index === currentIndex 
                    ? 'w-6 bg-white' 
                    : 'w-2 bg-white/50 hover:bg-white/70'
                }`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

